import { Accordion, Badge, Group, Stack, Text } from '@mantine/core';
import { useBaselineCalculation } from '../../../hooks/useBaselineCalculation';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSession } from '../../../hooks/useSession';

interface DebugPhaseAccordionProps {
  showIcon?: boolean;
  showLabel?: boolean;
}

export default function PhaseDetailsAccordion({
  showIcon = true,
  showLabel = true,
}: DebugPhaseAccordionProps) {
  const { id } = useParams();
  const { t } = useTranslation();
  const { session } = useSession(id);
  const phase = useHeartRateStore(s => s.phase);
  const dataState = useHeartRateStore(s => s.events);
  const { baseline } = useBaselineCalculation();
  const variation = session?.config?.variation;
  const currentState = session?.config?.currentState;

  const latestEvent = dataState.at(-1);
  const latestHr = latestEvent?.hr || 0;

  const ratioFromBaseline = baseline > 0 ? (latestHr - baseline) / baseline : 0;

  const effectiveVariation = variation === 0 ? 5 : variation || 5;
  const thresholds = {
    p2: (effectiveVariation * 2) / 100,
    p1: effectiveVariation / 100,
    p0: 0,
    'p-1': -effectiveVariation / 100,
    'p-2': -((effectiveVariation * 2) / 100),
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'p2':
        return 'red';
      case 'p1':
        return 'orange';
      case 'p0':
        return 'blue';
      case 'p-1':
        return 'cyan';
      case 'p-2':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const currentPhase = baseline > 0 ? phase : currentState || 'p0';

  return (
    <Accordion variant='contained'>
      <Accordion.Item value='debug-phase'>
        <Accordion.Control>
          <Group gap='sm'>
            {showIcon && <Activity size={16} />}
            {showLabel && (
              <Text size='sm' fw={500}>
                {t('current_state', 'Stato corrente')}
              </Text>
            )}
            {currentState && (
              <Badge color={getPhaseColor(currentPhase)} size='sm'>
                {currentPhase.toUpperCase()}
              </Badge>
            )}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap='xs' p='xs'>
            <Group justify='space-between'>
              <Text size='xs'>{t('base_phase', 'Base Phase')}:</Text>
              {currentState && (
                <Badge color={getPhaseColor(currentState)}>
                  {currentState.toUpperCase()}
                </Badge>
              )}
            </Group>

            <Group justify='space-between'>
              <Text size='xs'>{t('baseline', 'Baseline')}:</Text>
              <Text size='xs' fw={500}>
                {baseline} bpm
              </Text>
            </Group>

            <Group justify='space-between'>
              <Text size='xs'>{t('latest_hr', 'Latest HR')}:</Text>
              <Text size='xs' fw={500}>
                {latestHr} bpm
              </Text>
            </Group>

            <Group justify='space-between'>
              <Text size='xs'>{t('variation', 'Variation')}:</Text>
              <Text size='xs' fw={500}>
                {variation === undefined
                  ? 'undefined'
                  : `${variation}% (using ${effectiveVariation}%)`}
              </Text>
            </Group>

            <Group justify='space-between'>
              <Text size='xs'>
                {t('ratio_from_baseline', 'Ratio from Baseline')}:
              </Text>
              <Text size='xs' fw={500}>
                {(ratioFromBaseline * 100).toFixed(2)}%
              </Text>
            </Group>

            <Group justify='space-between'>
              <Text size='xs'>{t('total_events', 'Total Events')}:</Text>
              <Text size='xs' fw={500}>
                {dataState.length}
              </Text>
            </Group>

            <div style={{ marginTop: '8px' }}>
              <Text size='xs' fw={600} mb='xs'>
                {t('thresholds', 'Thresholds')}:
              </Text>
              <Stack gap='2px'>
                <Group justify='space-between'>
                  <Text size='xs'>
                    P2: &gt; {(thresholds.p2 * 100).toFixed(1)}%
                  </Text>
                  <Badge
                    size='xs'
                    color={ratioFromBaseline > thresholds.p2 ? 'red' : 'gray'}
                  >
                    P2
                  </Badge>
                </Group>
                <Group justify='space-between'>
                  <Text size='xs'>
                    P1: &gt; {(thresholds.p1 * 100).toFixed(1)}%
                  </Text>
                  <Badge
                    size='xs'
                    color={
                      ratioFromBaseline > thresholds.p1 &&
                      ratioFromBaseline <= thresholds.p2
                        ? 'orange'
                        : 'gray'
                    }
                  >
                    P1
                  </Badge>
                </Group>
                <Group justify='space-between'>
                  <Text size='xs'>
                    P0: ±{(thresholds.p1 * 100).toFixed(1)}%
                  </Text>
                  <Badge
                    size='xs'
                    color={
                      ratioFromBaseline >= thresholds['p-1'] &&
                      ratioFromBaseline <= thresholds.p1
                        ? 'blue'
                        : 'gray'
                    }
                  >
                    P0
                  </Badge>
                </Group>
                <Group justify='space-between'>
                  <Text size='xs'>
                    P-1: &lt; {(thresholds['p-1'] * 100).toFixed(1)}%
                  </Text>
                  <Badge
                    size='xs'
                    color={
                      ratioFromBaseline < thresholds['p-1'] &&
                      ratioFromBaseline >= thresholds['p-2']
                        ? 'cyan'
                        : 'gray'
                    }
                  >
                    P-1
                  </Badge>
                </Group>
                <Group justify='space-between'>
                  <Text size='xs'>
                    P-2: &lt; {(thresholds['p-2'] * 100).toFixed(1)}%
                  </Text>
                  <Badge
                    size='xs'
                    color={
                      ratioFromBaseline < thresholds['p-2'] ? 'purple' : 'gray'
                    }
                  >
                    P-2
                  </Badge>
                </Group>
              </Stack>
            </div>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
